/**
 * 单项校验状态标签（OCR / 企查查 / 税务局 / 合同比对）
 *
 * UNAVAILABLE 是最关键的一档：文档七要求「标记外部校验暂不可用，
 * 允许人工临时接管，不卡死业务」，因此这里用醒目的警告样式 + 明确后缀，
 * 提示操作人走人工接管通道。
 */

import { Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DisconnectOutlined,
  HourglassOutlined,
  LoadingOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { CheckStatus, CHECK_STATUS_LABELS } from '@ilc/shared';

const STATUS_COLORS: Record<CheckStatus, string> = {
  [CheckStatus.PENDING]: 'default',
  [CheckStatus.RUNNING]: 'processing',
  [CheckStatus.SUCCESS]: 'success',
  [CheckStatus.FAILED]: 'error',
  // 醒目但不代表失败：能力缺口，业务可继续
  [CheckStatus.UNAVAILABLE]: 'warning',
  [CheckStatus.MANUAL_OVERRIDE]: 'purple',
};

const STATUS_ICONS: Record<CheckStatus, React.ReactNode> = {
  [CheckStatus.PENDING]: <HourglassOutlined />,
  [CheckStatus.RUNNING]: <LoadingOutlined />,
  [CheckStatus.SUCCESS]: <CheckCircleOutlined />,
  [CheckStatus.FAILED]: <CloseCircleOutlined />,
  [CheckStatus.UNAVAILABLE]: <DisconnectOutlined />,
  [CheckStatus.MANUAL_OVERRIDE]: <UserSwitchOutlined />,
};

const STATUS_HINTS: Partial<Record<CheckStatus, string>> = {
  [CheckStatus.UNAVAILABLE]:
    '外部接口不可用，本项自动校验已跳过。可点击「人工核验并接管」填写核验说明后继续，业务不因此卡死。',
  [CheckStatus.MANUAL_OVERRIDE]: '已由人工核验并填写说明，记录已留痕。',
  [CheckStatus.FAILED]: '校验执行失败，可点击「重试」重新发起；仍失败可人工接管。',
};

export interface CheckStatusTagProps {
  status: CheckStatus | null | undefined;
  showIcon?: boolean;
  /** 校验项名称，用于 tooltip 文案 */
  itemName?: string;
}

export function CheckStatusTag({
  status,
  showIcon = true,
  itemName,
}: CheckStatusTagProps): React.ReactElement {
  // null/undefined = 这一项**还没跑过**，不是"跑失败了"。
  // 写成「未知」会让用户以为是识别出错，实际往往是发票还没提交校验。
  if (!status) return <Tag>未开始</Tag>;
  const label = CHECK_STATUS_LABELS[status] ?? status;
  const hint = STATUS_HINTS[status];
  const tag = (
    <Tag color={STATUS_COLORS[status] ?? 'default'} icon={showIcon ? STATUS_ICONS[status] : undefined}>
      {label}
    </Tag>
  );
  if (!hint) return tag;
  return <Tooltip title={itemName ? `【${itemName}】${hint}` : hint}>{tag}</Tooltip>;
}

export default CheckStatusTag;
